/**
 * Checkout Module
 *
 * Central hub for checkout functionality.
 *
 * Architecture:
 * - Markets: Country-specific behaviors (tax, address, etc.)
 * - Payments: Payment method plugins
 * - Features: Optional features (gift wrapping, etc.)
 * - Errors: Comprehensive error handling
 *
 * Usage:
 *
 * ```typescript
 * import {
 *   // Markets
 *   getMarketBehavior,
 *   createMarketContext,
 *
 *   // Payments
 *   paymentRegistry,
 *   getAvailablePaymentMethods,
 *
 *   // Features
 *   featureRegistry,
 *   getAvailableFeatures,
 *
 *   // Errors
 *   handleCheckoutError,
 *   parseSaleorErrors,
 * } from '@/lib/checkout';
 *
 * // Get market context with config
 * const marketContext = createMarketContext(
 *   'US',
 *   checkoutConfig,
 *   moduleActivation
 * );
 *
 * // Get available payment methods
 * const paymentMethods = getAvailablePaymentMethods({
 *   marketContext,
 *   amount: 1000,
 *   isSplitPayment: false,
 *   existingPayments: [],
 * });
 *
 * // Get available features
 * const features = getAvailableFeatures({
 *   marketContext,
 *   lineItems: checkout.lines,
 * });
 * ```
 */

// ============================================================================
// Markets
// ============================================================================

// ============================================================================
// Errors
// ============================================================================
export {
  ADYEN_ERROR_CODES,
  // Error handling
  createCheckoutError,
  errorResult,
  // Messages
  getErrorMessage,
  // Error codes
  getErrorMeta,
  getFieldErrors,
  getFirstErrorMessage,
  getRetryConfig,
  handleCheckoutError,
  handleCheckoutErrors,
  hasRetryableErrors,
  isAddressError,
  isErrorResult,
  isKnownErrorCode,
  isPaymentError,
  isRecoverableError,
  isRetryableError,
  isStockError,
  isSuccessResult,
  // Type guards
  isValidationError,
  ORDER_APP_ERROR_CODES,
  parseNetworkError,
  parseOrderAppErrors,
  parseSaleorErrors,
  SALEOR_ERROR_CODES,
  withRetry,
  type AddressError,
  // Types
  type CheckoutError,
  type CheckoutErrorResult,
  type CheckoutResult,
  type CheckoutSuccessResult,
  type ErrorCategory,
  type ErrorSeverity,
  type PaymentError,
  type StockError,
  type ValidationError,
} from './errors';
// ============================================================================
// Express Checkout
// ============================================================================
export {
  // Error codes
  EXPRESS_CHECKOUT_ERRORS,
  // Hook
  useExpressCheckout,
  type ExpressCheckoutState,
  // Types
  type UseExpressCheckoutOptions,
  type UseExpressCheckoutResult,
} from './express';
// ============================================================================
// Features
// ============================================================================
export {
  // Registry
  featureRegistry,
  getAvailableFeatures,
  getFeature,
  type CheckoutFeaturesState,
  type FeatureAvailability,
  type FeatureCategory,
  // Types
  type FeatureModule,
  type FeatureState,
} from './features';
export {
  createMarketBehavior,
  createMarketContext,

  // Defaults
  defaultMarketBehavior,
  getChannelSlug,
  getCurrency,
  getMarketBehavior,
  // Registry
  marketRegistry,
  type AddressFormat,
  type DeliveryConfig,
  type LegalConfig,
  type MarketBehavior,
  type MarketContext,
  type TaxConfig,
} from './markets';
// ============================================================================
// Payments
// ============================================================================
export {
  getAvailablePaymentMethods,
  getPaymentMethod,
  // Registry
  paymentRegistry,
  type PaymentAvailability,
  // Types
  type PaymentMethod,
  type PaymentMethodCategory,
  type PaymentMethodConfig,
  type PaymentResult,
  type PaymentState,
  type SplitPaymentConfig,
} from './payments';
// ============================================================================
// Split Payments
// ============================================================================
export {
  calculateSplitRange,
  calculateSplitSummary,
  canSplitOrder,
  getValidationErrorMessage,
  // Error codes
  SPLIT_VALIDATION_ERRORS,
  // Validation functions
  validateSplitAmount,
  // Types
  type PaymentRecord,
  type SplitPaymentState,
  type SplitValidationParams,
  type SplitValidationResult,
} from './payments/split';

// ============================================================================
// Constants
// ============================================================================
export {
  CA_PROVINCES,
  COUNTRIES,
  countryRequiresState,
  getStatesForCountry,
  US_STATES,
  type Country,
  type StateProvince,
} from './constants';

// ============================================================================
// Japan-Specific Constants
// ============================================================================
export {
  getJpPrefecturesForLocale,
  getPrefectureDisplayName,
  isJapanCountry,
  isJapanLocale,
  JP_DELIVERY_TIME_SLOTS,
  JP_PREFECTURES,
  type Prefecture,
} from './constants';

// ============================================================================
// Address Form Types
// ============================================================================
export {
  DELIVERY_MODES,
  EMPTY_ADDRESS_FORM,
  EMPTY_BILLING_FORM,
  EMPTY_JP_FORM,
  type AddressFormData,
  type AddressFormErrors,
  type BillingAddressFormData,
  type JpFormData,
  type Salutation,
  type ShippingAddressFormData,
} from './types';
